import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, MapPin, Clock, Banknote, Briefcase, ArrowRight, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface JobSection {
  key: string;
  intro?: string;
  items?: string[];
  paragraphs?: string[];
}

interface Job {
  id: string;
  title: string;
  type: string;
  compensation: string;
  hours: string;
  location: string;
  tagline: string;
  accent: string;
  aboutCevee: string[];
  aboutRole: string[];
  sections: JobSection[];
  applyNoteKey: string;
}

const jobs: Job[] = [
  {
    id: 'ambassador',
    title: 'Cevee Ambassador (Minijob / Werkstudent:in)',
    type: 'Minijob / Werkstudent:in',
    compensation: '€20/Stunde',
    hours: '3-5 Stunden/Woche',
    location: 'Remote / Deutschland',
    tagline: 'Shape the future of careers with Cevee.',
    accent: 'from-pink-500 to-yellow-500',
    aboutCevee: [
      'Cevee ist die AI-native Karriereplattform für Gen Z: Explore echte Karrierewege, Practice Interviews privat, Inspire andere mit deiner Story, und über Hire von passenden Unternehmen gefunden werden.',
      'Bereits 1.000+ Talente sind dabei. Im Kern geht es um echte Menschen und echte Wege; AI macht Karriere weniger scary, nicht komplizierter.'
    ],
    aboutRole: [
      'Die Rolle ist eine Mischung aus Campus Ambassador, Content Creator und Produkt-Stimme - je nach deinen Stärken kann der Fokus auf Community, Content oder Produktfeedback liegen.',
      'Du bringst Cevee in studentische Communities, erstellst authentischen Short-Form Content und gibst ehrliches Feedback darüber, was Studierende wirklich beschäftigt und hilft.'
    ],
    sections: [
      {
        key: 'whatYoullDoAmbassador',
        items: [
          'Cevee in studentischen Communities, Freundeskreisen und Campus-Netzwerken bekannt machen',
          'Kurze Videos für Cevees Social-Media-Kanäle erstellen',
          'Content zu echten Student:innen-Momenten, Karriere-Unsicherheit und relatable Pain Points - Memes, POVs, Mini-Skits, alles was passt',
          'Cevees Features (Explore, Practice, Inspire) authentisch in deinem Content zeigen',
          'Regelmäßig Feedback geben: Was beschäftigt Studierende? Was sollte Cevee bauen?'
        ]
      },
      {
        key: 'whoYouAre',
        items: [
          'Aktuell als Student:in in Deutschland eingeschrieben',
          'Aktiv in studentischen Communities, online oder offline',
          'Gutes Gefühl dafür, was bei Gen Z ankommt',
          'Kannst einfache, authentische Videos drehen - ohne großes Setup',
          'Kannst ca. 3-5 Stunden pro Woche investieren'
        ],
        paragraphs: [
          'Du musst kein:e professionelle:r Creator:in sein. Uns ist Authentizität, Kreativität und Verständnis für Studierende wichtiger als perfekter Schnitt :)'
        ]
      },
      {
        key: 'whatYoullGet',
        items: [
          '€20/Stunde, 3-5h/Woche (Minijob/Werkstudent:in)',
          'Du arbeitest direkt mit den Gründer:innen',
          'Echten Einfluss darauf, wie Cevee wächst',
          'Front-Row-Seat beim Startup-Aufbau von Tag 1'
        ]
      }
    ],
    applyNoteKey: 'apply.ambassadorNote'
  },
  {
    id: 'fullstack-ai',
    title: 'Fullstack AI Engineer (Werkstudent:in)',
    type: 'Werkstudent:in',
    compensation: '€20/Stunde',
    hours: 'Flexible Zeiten',
    location: 'Remote / Deutschland',
    tagline: 'Build the AI-native career platform for Gen Z.',
    accent: 'from-violet-500 to-pink-500',
    aboutCevee: [
      'Cevee ist die AI-native Karriereplattform für Gen Z: Explore, Practice, Inspire, Hire in einem Produkt. Studierende entdecken Wege, üben Interviews privat, teilen ihre Story und werden von passenden Unternehmen gefunden.',
      'Unter der Haube bauen wir echte AI-Systeme quer durchs Produkt: semantische Suche und Karriere-Matching (Explore), CV-Intelligence und Profil-Feedback (Inspire), AI-Talent-Sourcing für Unternehmen (Hire). Für Practice & Hire dazu: Conversational AI, Live-Coding-Panels, Whiteboard und Scorecards. Bereits 1.000+ Talente nutzen Cevee. AI ist Foundation, im Kern geht es um echte Karrierewege.'
    ],
    aboutRole: [
      'Das hier ist vor allem eins: eine riesige Lernchance. Du arbeitest direkt mit dem CTO zusammen (kommt aus der AI-Forschung an Harvard & RWTH und hat bei Uber, Google und Amazon Produkte gebaut und skaliert), bekommst 1:1 Mentorship und lernst hands-on, wie ein AI-first Startup echte Produkte baut.',
      'Du musst nicht alles können - du suchst dir aus, worauf du Lust hast und was du lernen willst. Frontend, Backend, Infrastruktur, AI-Features, LLM-Workflows, Agentic Systems - du pickst dir die Themen, die dich am meisten reizen, und wächst da rein.'
    ],
    sections: [
      {
        key: 'whatYoullDo',
        intro: 'Du musst nicht alles machen - such dir aus, was dich am meisten reizt:',
        items: [
          'Product Features im Frontend oder Backend bauen',
          'AI-powered Search, Matching oder Recommendations mitentwickeln',
          'Am AI Interview Stack mitarbeiten: Practice (Simulationen) und Hire (Recruiting-Interviews), Conversational AI, Live-Coding-Panels, Whiteboard, Scorecards',
          'Neue AI-native Features prototypen und shippen',
          'An Datenbanken, APIs oder Infrastruktur mitarbeiten',
          'Direkt mit dem CTO an Produkt- und technischen Entscheidungen arbeiten'
        ]
      },
      {
        key: 'whoYouAre',
        items: [
          'Aktuell als Student:in in Deutschland eingeschrieben',
          'Neugierig und motiviert, Neues zu lernen',
          'Baust gerne Dinge und probierst Sachen aus',
          'Hast Interesse an mindestens einem der Bereiche: Frontend, Backend, AI oder Infrastruktur'
        ]
      },
      {
        key: 'niceToHave',
        items: [
          'Erste Berührungspunkte mit Frontend- oder Backend-Entwicklung',
          'Interesse an AI, LLMs oder Agentic Systems',
          'Eigene Side Projects, egal wie klein',
          'Erfahrung mit Datenbanken, Cloud oder Infrastruktur'
        ],
        paragraphs: [
          'Du musst nichts davon schon können. Uns ist Neugier, Eigeninitiative und Lust aufs Lernen viel wichtiger als Vorerfahrung :)'
        ]
      },
      {
        key: 'whatYoullGet',
        items: [
          '€20/Stunde, flexible Arbeitszeiten',
          '1:1 Mentorship und direkte Zusammenarbeit mit dem CTO',
          'Du wählst deine Schwerpunkte selbst - lerne, was dich am meisten interessiert',
          'Echtes Ownership und Impact von Tag 1',
          'Hands-on Erfahrung mit State-of-the-Art AI-Systemen in einem echten Produkt'
        ]
      }
    ],
    applyNoteKey: 'apply.engineerNote'
  }
];

const Careers: React.FC = () => {
  const { t } = useTranslation('careers');
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleJob = (jobId: string) => {
    setExpandedJob(prev => prev === jobId ? null : jobId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-white">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 via-yellow-500/10 to-violet-500/10" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-5 sm:pt-12 sm:pb-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2.5">
              <span className="text-gray-900">{t('hero.title')}</span>{' '}
              <span className="bg-gradient-to-r from-pink-600 via-pink-500 to-yellow-500 bg-clip-text text-transparent">
                {t('hero.titleHighlight')}
              </span>
            </h1>
            <p className="text-sm sm:text-base text-gray-600 max-w-xl mx-auto leading-relaxed">
              {t('hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Jobs */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-10">
        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-base sm:text-lg font-bold text-gray-900 mb-4"
        >
          {t('openRoles')}
        </motion.h2>

        <div className="space-y-3">
          {jobs.map((job, index) => {
            const isExpanded = expandedJob === job.id;
            return (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              >
                {/* Card */}
                <div className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-all border ${isExpanded ? 'border-pink-200' : 'border-gray-100'} overflow-hidden`}>
                  {/* Header - always visible */}
                  <button
                    onClick={() => toggleJob(job.id)}
                    className="w-full text-left p-3.5 sm:p-5 focus:outline-none"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${job.accent} flex-shrink-0`} />
                          <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate">
                            {job.title}
                          </h3>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 mb-2">
                          {job.tagline}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded-full">
                            <Banknote className="w-3 h-3 text-gray-400" />
                            {job.compensation}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded-full">
                            <Clock className="w-3 h-3 text-gray-400" />
                            {job.hours}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded-full">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            {job.location}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded-full">
                            <Briefcase className="w-3 h-3 text-gray-400" />
                            {job.type}
                          </span>
                        </div>
                      </div>
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-0.5 flex-shrink-0"
                      >
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      </motion.div>
                    </div>
                  </button>

                  {/* Expanded content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-3.5 sm:px-5 pb-4 sm:pb-5 space-y-4 border-t border-gray-100 pt-3.5 sm:pt-4">
                          {/* About Cevee */}
                          <div>
                            <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-1.5">
                              {t('aboutCevee')}
                            </h4>
                            <div className="space-y-1.5">
                              {job.aboutCevee.map((p, i) => (
                                <p key={i} className="text-xs sm:text-sm text-gray-600 leading-relaxed">{p}</p>
                              ))}
                            </div>
                          </div>

                          {/* About the role */}
                          <div>
                            <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-1.5">
                              {t('aboutRole')}
                            </h4>
                            <div className="space-y-1.5">
                              {job.aboutRole.map((p, i) => (
                                <p key={i} className="text-xs sm:text-sm text-gray-600 leading-relaxed">{p}</p>
                              ))}
                            </div>
                          </div>

                          {/* Dynamic sections */}
                          {job.sections.map((section) => (
                            <div key={section.key}>
                              <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-1.5">
                                {t(section.key)}
                              </h4>
                              {section.intro && (
                                <p className="text-xs sm:text-sm text-gray-600 mb-1.5">{section.intro}</p>
                              )}
                              {section.items && (
                                <ul className="space-y-1">
                                  {section.items.map((item, i) => (
                                    <li key={i} className="flex items-start gap-1.5 text-xs sm:text-sm text-gray-600">
                                      <span className="mt-1.5 w-1 h-1 rounded-full bg-pink-400 flex-shrink-0" />
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {section.paragraphs && (
                                <div className={`space-y-1.5 ${section.items ? 'mt-2' : ''}`}>
                                  {section.paragraphs.map((p, i) => (
                                    <p key={i} className="text-xs sm:text-sm text-gray-500 italic">{p}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}

                          {/* How to apply */}
                          <div>
                            <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-1.5">
                              {t('howToApply')}
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600 mb-2">
                              {t(job.applyNoteKey)}
                            </p>
                            <Link
                              to="/?tab=inspire"
                              className={`inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r ${job.accent} text-white font-semibold rounded-full hover:opacity-90 transition-opacity shadow-sm text-xs sm:text-sm`}
                            >
                              <Upload className="w-3.5 h-3.5" />
                              {t('apply.cta')}
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-center"
        >
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-5 sm:p-8">
            <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">
              {t('cta.title')}
            </h3>
            <p className="text-gray-400 text-xs sm:text-sm mb-4 max-w-md mx-auto">
              {t('cta.subtitle')}
            </p>
            <Link
              to="/?tab=inspire"
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-colors text-xs sm:text-sm"
            >
              {t('cta.button')}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Careers;
